import { Component } from "@angular/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { MissionService } from "./services/mission.service";
import {
  AlertButton,
  AlertController,
  NavController,
  Platform,
} from "@ionic/angular";
import { Preferences, Preferences as Storage } from "@capacitor/preferences";
import { NetworkService } from "./services/network.service";
import { Version } from "src/app/services/constantes.service";
import { AuthService } from "./services/auth.service";
import {
  AppUpdate,
  AppUpdateAvailability,
} from "@capawesome/capacitor-app-update";
import { Capacitor } from "@capacitor/core";
import { SurveyService } from "./services/survey.service";
import { StatusBar, Style } from "@capacitor/status-bar";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";
import { PhotoCacheService } from "./services/photo-cache.service";
import { FacturationDataService } from "./services/facturation-data.service";
import { PdvCacheService } from "./services/pdv-cache.service";
import { PdvPreloadService } from "./services/pdv-preload.service";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: false,
})
export class AppComponent {
  version: string;

  /** Accordéon « Ma facturation » du menu : sous-rubriques masquées par défaut (FR-165). */
  facturationOpen = false;

  constructor(
    private navCtrl: NavController,
    protected missionService: MissionService,
    private alertCtrl: AlertController,
    protected authService: AuthService,
    protected networkService: NetworkService,
    private surveyService: SurveyService,
    private platform: Platform,
    private photoCacheService: PhotoCacheService,
    private facturationData: FacturationDataService,
    private pdvCache: PdvCacheService,
    private pdvPreload: PdvPreloadService
  ) {
    this.version = Version;
    this.networkService.statusChanged.subscribe(async (connected) => {
      if (connected && !(await this.missionService.hasPendingMissions())) {
        await this.surveyService.sendSurveys();
      }
    });
    this.initializeApp().then(() => {
      this.authService.loggedIn().then((isLoggedIn) => {
        // FR-179 : préchargement en tâche de fond des listes de PDV hors
        // fichier — la liste ne s'actualise qu'à l'ouverture de l'appli.
        if (isLoggedIn) {
          this.pdvPreload.precharger();
        }
        Storage.get({
          key: "last_sync",
        }).then((data) => {
          if (data.value) {
            const last_sync = new Date(data.value);
            const now = new Date();
            const diff = now.getTime() - last_sync.getTime();
            const diffHours = diff / (1000 * 3600);
            if (diffHours > 24 && isLoggedIn && this.networkService.isOnline) {
              this.missionService.presentSyncroModal();
            }
          } else if (isLoggedIn && this.networkService.isOnline) {
            this.missionService.presentSyncroModal();
          }
          Storage.set({
            key: "last_sync",
            value: new Date().toISOString(),
          });
        });
      });
    });
  }

  async initializeApp() {
    if (Capacitor.getPlatform() === "android") {
      EdgeToEdge.setBackgroundColor({ color: "#e5236a" });
      StatusBar.setStyle({ style: Style.Light });
    }
    this.platform.ready().then(() => {
      SplashScreen.hide();
      this.checkForUpdates();
    });
  }

  async checkForUpdates() {
    if (
      Capacitor.getPlatform() !== "android" &&
      Capacitor.getPlatform() !== "ios"
    ) {
      return;
    }

    try {
      let result = await AppUpdate.getAppUpdateInfo();

      const currentVersion =
        Capacitor.getPlatform() === "android"
          ? result.currentVersionCode
          : result.currentVersionName;

      // Get the stored version from the preferences
      const { value: storedVersion } = await Preferences.get({
        key: "appVersion",
      });

      // If the stored version is different from the current version, reset the updateCancelCount
      if (storedVersion !== currentVersion) {
        await Preferences.set({ key: "updateCancelCount", value: "0" });
        await Preferences.set({ key: "appVersion", value: currentVersion });
      }

      if (
        result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE
      ) {
        return;
      }

      // Get the current count from the preferences
      let { value } = await Preferences.get({ key: "updateCancelCount" });
      let updateCancelCount = value ? parseInt(value) : 0;
      let buttons: AlertButton[] = [
        {
          text: "Mettre à jour",
          handler: () => {
            AppUpdate.openAppStore();
          },
        },
      ];

      if (updateCancelCount < 3) {
        buttons.unshift({
          text: "Annuler",
          role: "cancel",
          handler: () => {
            // Increment the counter when the user cancels the update
            updateCancelCount++;
            Preferences.set({
              key: "updateCancelCount",
              value: updateCancelCount.toString(),
            });
          },
        });
      }

      this.alertCtrl
        .create({
          header: "Nouvelle version disponible",
          message: "Une nouvelle version de l'application est disponible.",
          buttons: buttons,
        })
        .then((alert) => alert.present());
    } catch (e) {
      this.alertCtrl
        .create({
          header: "Erreur",
          message: "Impossible de vérifier les mises à jour: " + e,
          buttons: ["OK"],
        })
        .then((alert) => alert.present());
    }
  }

  logout() {
    this.alertCtrl
      .create({
        message: "Voulez-vous vous déconnecter ?",
        buttons: [
          {
            text: "Non",
            handler: () => {
              console.log("annuler");
            },
          },
          {
            text: "Oui",
            handler: async () => {
              // Purge des caches mémoire AVANT d'effacer la session : les clés de
              // persistance de la facturation se déduisent du compte courant.
              // Sans cela, montants et préfacturations d'un intervenant restaient
              // pré-affichables sous le compte suivant sur un appareil partagé
              // (review Greptile PR #300). PdvCacheService n'était vidé nulle part
              // malgré sa documentation.
              this.facturationData.invalider();
              this.pdvCache.clear();
              this.pdvPreload.reset();
              await Storage.remove({ key: "filrouge_user" });
              await Storage.remove({ key: "id_token_filrouge2new" });
              await Storage.remove({ key: "fr_aenvoyer" });
              await Storage.remove({ key: "last_sync" });
              await Storage.remove({ key: "filrouge_delete" });
              await Storage.remove({ key: "missions" });
              await Storage.remove({ key: "fr_list_fichiers" });
              await this.photoCacheService.clear();
              await this.navCtrl.navigateBack(["/connexion"]);
            },
          },
        ],
      })
      .then((alert) => {
        alert.present();
      });
  }
}
